import React, { useState, useEffect } from 'react';
import { authFetch, getUser, deleteSsoAccount } from './auth';
import { useI18n } from './i18n';

const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:8100/api' : '/api';

const ASSOCIATIONS = ['ONAV', 'ASPI', 'AIS', 'FISAR', 'FIS', 'Slow Food', 'other'];

function ProfilePage() {
  const { t } = useI18n();
  const kcUser = getUser();
  const [affiliations, setAffiliations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/me`);
        if (res && res.ok) {
          const data = await res.json();
          setAffiliations((data.affiliations || []).map((a) => ({
            association: a.association || '', member_number: a.member_number || '',
          })));
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const addAffiliation = () => {
    setAffiliations([...affiliations, { association: '', member_number: '' }]);
  };

  const updateAffiliation = (idx, field, value) => {
    setAffiliations(affiliations.map((a, i) => (i === idx ? { ...a, [field]: value } : a)));
  };

  const removeAffiliation = (idx) => {
    setAffiliations(affiliations.filter((_, i) => i !== idx));
  };

  const save = async () => {
    try {
      const res = await authFetch(`${API_URL}/me`, {
        method: 'PUT',
        body: JSON.stringify({ affiliations: affiliations.filter((a) => a.association) }),
      });
      if (res && res.ok) {
        setMessage({ type: 'success', text: t('profile.saved') });
      } else {
        setMessage({ type: 'error', text: t('profile.error_save') });
      }
    } catch {
      setMessage({ type: 'error', text: t('profile.error_conn') });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  const deleteAccount = async () => {
    const ok = window.confirm(t('profile.delete_confirm'));
    if (!ok) return;
    try {
      const res = await authFetch(`${API_URL}/me`, { method: 'DELETE' });
      if (!res || !res.ok) throw new Error();
      sessionStorage.setItem('tnh_account_deleted', '1');
      deleteSsoAccount();   // redirect to Keycloak to delete the credentials as well
    } catch {
      setMessage({ type: 'error', text: t('profile.error_delete') });
      setTimeout(() => setMessage(null), 4000);
    }
  };

  if (loading) return <div className="empty-state">{t('profile.loading')}</div>;

  return (
    <div className="detail">
      <h2>{t('profile.title')}</h2>
      <div className="form-grid">
        <div className="form-field">
          <label>{t('profile.first_name')}</label>
          <input value={kcUser?.firstName || ''} disabled />
        </div>
        <div className="form-field">
          <label>{t('profile.last_name')}</label>
          <input value={kcUser?.lastName || ''} disabled />
        </div>
        <div className="form-field">
          <label>{t('profile.email')}</label>
          <input value={kcUser?.email || ''} disabled />
        </div>
      </div>
      <p style={{ fontSize: '0.75rem', color: '#999', marginTop: 4 }}>
        {t('profile.sso_note')}
      </p>

      <h3>{t('profile.affiliations')}</h3>
      {affiliations.map((aff, i) => (
        <div key={i} className="affiliation-row">
          <select value={aff.association}
            onChange={(e) => updateAffiliation(i, 'association', e.target.value)}>
            <option value="">{t('profile.select')}</option>
            {ASSOCIATIONS.map(a => <option key={a} value={a}>{a === 'other' ? t('profile.other') : a}</option>)}
          </select>
          <input placeholder={t('profile.card_number')} value={aff.member_number}
            onChange={(e) => updateAffiliation(i, 'member_number', e.target.value)} />
          <button type="button" className="delete-btn" style={{ padding: '4px 10px', marginTop: 0 }}
            onClick={() => removeAffiliation(i)}>X</button>
        </div>
      ))}
      <button type="button" className="chip" onClick={addAffiliation}
        style={{ marginTop: 8 }}>{t('profile.add')}</button>

      {message && <div className={`message ${message.type}`} style={{ marginTop: 12 }}>{message.text}</div>}

      <button className="submit-btn" style={{ marginTop: 16 }} onClick={save}>
        {t('profile.save_btn')}
      </button>

      <h3 style={{ marginTop: 32 }}>{t('profile.delete_title')}</h3>
      <p style={{ fontSize: '0.85rem', color: '#777' }}>
        {t('profile.delete_desc')}
      </p>
      <button className="delete-btn" onClick={deleteAccount}>{t('profile.delete_btn')}</button>
    </div>
  );
}

export default ProfilePage;
